import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { captureException } from '@sentry/react';
import { useClientUser } from '@/hooks';
import { CREATE_TARIFF, DELETE_TARIFF, DUPLICATE_PRICING_TARIFF, LIST_TARIFFS_BY_CLIENT_ID } from '../Tariffs/graphql';
import { useTariffRoutes } from '../Tariffs/routes';
import {
    algoOptionsFor,
    createTariffPayload,
    defaultMappingFor,
    defaultOverrideFor,
    driverMappingPayload,
    pricingOverridePayload,
    tariffsOfType,
} from './utils';

/**
 * State and mutations for the tariff table. `tariffs`, `overrides` and `driverMappings` come
 * from useTariffBundle at the call site; `callbacks` carries its two cache-updating upserts,
 * which is why they are required rather than owned here.
 */
export const useTariffTable = ({ tariffs, overrides, driverMappings, callbacks }) => {
    const navigate = useNavigate();
    const { user_id, writableTariffClientIds } = useClientUser();
    const { editPath } = useTariffRoutes();
    const [tariffType, setTariffType] = useState('ORDER');
    const [algo, setAlgo] = useState(null);
    const [deleting, setDeleting] = useState(null);

    const algoTypes = useMemo(() => algoOptionsFor(tariffType, overrides), [overrides, tariffType]);
    const defaultOverride = useMemo(() => defaultOverrideFor(overrides), [overrides]);
    const defaultMapping = useMemo(() => defaultMappingFor(driverMappings), [driverMappings]);
    const defaultObj = tariffType === 'DRIVER' ? defaultMapping : defaultOverride;
    const filtered = useMemo(() => tariffsOfType(tariffs, tariffType), [tariffs, tariffType]);

    const onError = (e) => {
        console.error(e);
        captureException(e);
    };

    const [createTariff] = useMutation(CREATE_TARIFF, {
        onCompleted: (resp) => {
            if (resp?.tariffs?.returning.length > 0) {
                navigate(editPath(resp.tariffs.returning[0].tariff_id));
            }
        },
        onError,
    });

    const [duplicateTariff] = useMutation(DUPLICATE_PRICING_TARIFF, {
        onCompleted: (resp) => {
            if (resp?.duplicated.length > 0) {
                navigate(editPath(resp.duplicated[0].tariff_id));
            }
        },
        onError,
    });

    const [deleteTariff, { loading: deleteLoading }] = useMutation(DELETE_TARIFF, {
        update: (cache, { data: { deleted } }) => {
            cache.updateQuery(
                { query: LIST_TARIFFS_BY_CLIENT_ID, variables: { client_ids: writableTariffClientIds } },
                (data) => {
                    if (!data) return data;
                    const { tariffs: cached, ...rest } = data;
                    return { ...rest, tariffs: cached.filter((t) => t.tariff_id !== deleted.tariff_id) };
                }
            );
        },
        onError,
    });

    const changeTariffType = (next) => {
        setTariffType(next);
        setAlgo(null);
    };

    const create = () => createTariff({ variables: createTariffPayload({ client_id: user_id, algo, tariffType }) });

    // The order tab's TariffLibrary hands back `{ algo_type, category, commodity_tags }` from its
    // own Add Tariff modal, and confirms deletes itself.
    const createOrderTariff = (fields) =>
        createTariff({ variables: createTariffPayload({ client_id: user_id, tariffType: 'ORDER', ...fields }) });

    // Repoint the account-wide default at this tariff. Driver and order tariffs are linked
    // through different tables, hence the two callbacks.
    const makeDefault = (tariff) => {
        if (tariffType === 'DRIVER') {
            return callbacks.upsertDriverTariff({
                variables: driverMappingPayload({ defaultMapping, client_id: user_id, tariff }),
            });
        }
        return callbacks.upsertPo({
            variables: pricingOverridePayload({ defaultOverride, client_id: user_id, tariff }),
        });
    };

    const confirmDelete = () => {
        if (deleting) deleteTariff({ variables: { tariff_id: deleting.tariff_id } });
        setDeleting(null);
    };

    return {
        state: {
            tariffType,
            algo,
            algoTypes,
            tariffs: filtered,
            defaultObj,
            deleting,
            loading: deleteLoading,
        },
        callbacks: {
            changeTariffType,
            setAlgo,
            create,
            createOrderTariff,
            makeDefault,
            duplicate: (tariff) => duplicateTariff({ variables: { tariff_id: tariff.tariff_id } }),
            edit: (tariff) => navigate(editPath(tariff.tariff_id)),
            askDelete: setDeleting,
            deleteById: (tariff_id) => deleteTariff({ variables: { tariff_id } }),
            cancelDelete: () => setDeleting(null),
            confirmDelete,
        },
    };
};
